import React from 'react';
import { MapContainer, TileLayer, Polygon, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix for default marker icon in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Farm Location: Upper Marlboro, MD
const FARM_CENTER = [38.8180, -76.7470];

// Define Fields (Polygons)
const FIELDS = [
    {
        id: 1,
        name: 'North Pivot',
        crop: 'Corn',
        color: 'green',
        positions: [
            [38.8200, -76.7500],
            [38.8220, -76.7480],
            [38.8210, -76.7440],
            [38.8185, -76.7460]
        ],
        status: 'Optimal'
    },
    {
        id: 2,
        name: 'River Bottom',
        crop: 'Soybeans',
        color: 'orange', // Warning status
        positions: [
            [38.8170, -76.7520],
            [38.8185, -76.7500],
            [38.8160, -76.7450],
            [38.8150, -76.7480]
        ],
        status: 'High Moisture'
    },
    {
        id: 3,
        name: 'Home Sector',
        crop: 'Wheat',
        color: 'blue',
        positions: [
            [38.8180, -76.7440],
            [38.8190, -76.7420],
            [38.8170, -76.7400],
            [38.8160, -76.7430]
        ],
        status: 'Harvest Ready'
    }
];

// Component to handle map interactions
function MapController({ onFieldSelect }) {
    return null;
}

export default function FarmMap({ onFieldSelect, selectedFieldId, fieldsData = [], className = "h-[600px]", mapVariant = "default" }) {
    // Merge static geometry with dynamic status
    const displayFields = FIELDS.map(staticField => {
        const dynamicField = fieldsData.find(f => f.id === staticField.id);
        if (dynamicField) {
            // Determine color based on health index if available
            let dynamicColor = staticField.color;
            if (dynamicField.healthIndex !== undefined) {
                if (dynamicField.healthIndex >= 80) dynamicColor = 'green';
                else if (dynamicField.healthIndex >= 60) dynamicColor = 'orange';
                else dynamicColor = 'red';
            }
            return {
                ...staticField,
                ...dynamicField, // properties from parent like name, crop override static
                color: dynamicColor,
                positions: staticField.positions // Ensure geometry is preserved
            };
        }
        return staticField;
    });

    const tileLayer = mapVariant === 'satellite'
        ? "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
        : "https://{s}.basemaps.cartocdn.com/rastertiles/voyager_labels_under/{z}/{x}/{y}{r}.png";

    const attribution = mapVariant === 'satellite'
        ? '&copy; <a href="https://www.esri.com/en-us/home">Esri</a>'
        : '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>';

    return (
        <div className={`${className} w-full rounded-3xl overflow-hidden glass border border-white/10 shadow-2xl relative z-10`}>
            <MapContainer
                center={FARM_CENTER}
                zoom={15}
                scrollWheelZoom={true}
                style={{ height: '100%', width: '100%' }}
            >
                <TileLayer
                    attribution={attribution}
                    url={tileLayer}
                />

                {displayFields.map((field) => (
                    <Polygon
                        key={field.id}
                        positions={field.positions}
                        pathOptions={{
                            color: field.id === selectedFieldId ? '#fff' : field.color,
                            fillColor: field.color,
                            fillOpacity: field.id === selectedFieldId ? 0.6 : 0.4,
                            weight: field.id === selectedFieldId ? 3 : 1
                        }}
                        eventHandlers={{
                            click: () => onFieldSelect(field),
                        }}
                    >
                        <Popup>
                            <div className="text-sm font-semibold text-gray-800">
                                {field.name}<br />
                                <span className="text-gray-500">{field.currentCrop || field.crop}</span><br />
                                <span className={`text-xs ${field.healthIndex >= 80 ? 'text-green-600' : 'text-orange-600'}`}>
                                    Health: {field.healthIndex ?? 'N/A'}%
                                </span>
                            </div>
                        </Popup>
                    </Polygon>
                ))}

                <Marker position={FARM_CENTER}>
                    <Popup>
                        Main Farmhouse<br />Upper Marlboro HQ
                    </Popup>
                </Marker>

                <MapController onFieldSelect={onFieldSelect} />
            </MapContainer>

            {/* Legend Overlay */}
            <div className="absolute bottom-6 right-6 glass p-4 rounded-xl z-[400]">
                <h4 className="text-xs font-bold text-muted-foreground uppercase mb-2">Map Legend</h4>
                <div className="space-y-2">
                    <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-green-500"></div>
                        <span className="text-xs">Healthy</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-orange-500"></div>
                        <span className="text-xs">Stress Detected</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                        <span className="text-xs">Action Required</span>
                    </div>
                </div>
            </div>
        </div>
    );
}
